import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  CardContent,
  Grid,
  Backdrop,
  TextField,
  Tooltip,
  Button,
  Link as MuiLink,
} from "@material-ui/core";
import moment from "moment";
import "moment-timezone";
import TabHeader from "src/components/TabHeader/TabHeader";
import { useStyles } from "./style";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonTextFieldDate from "src/components/CommonTextFieldForDate/CommonTextFieldDate";
import { useFormik } from "formik";
import { object, string } from "yup";
import { GridRowModel } from "@material-ui/x-grid";
import CommonButton from "src/components/CommonButton/CommonButton";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import MultiSelect2 from "src/components/molecules/MultiSelect2";
import { useAppSelector } from "src/app-redux/hooks";
import { selectPaymentTypes } from "src/app-redux/commercial/commercialSlice";
import { getPaymentTypesDropdownAction } from "src/app-redux/commercial/actions/commercialActions";
import { useDispatch } from "react-redux";
import { Check, Close } from "@material-ui/icons";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { getVehicleDetailsByIdAction } from "src/app-redux/vehicle/actions/vehicleActions";
import { loader as vehicleLoader, setSelectedVehicleId } from "src/app-redux/vehicle/vehicleSlice";
import Drawer from "src/modules/vehicle-management/pages/ListVehicles/components/Drawer";
import { Stack } from "@mui/material";
import CommonLoader from "src/components/Loader/CommonLoader";

interface Props {}

export function GenericReport({}: Props): React.ReactElement {
  const columns = [
    {
      accessorKey: "vehicleNumber",
      header: "Vehicle",
      type: "string",
      size: 140,
      accessorFn: (row: any) => ` ${row.vehicleNumber || " "}`,
    },
    {
      accessorKey: "webocVehicleNumber",
      header: "WeBOC Vehicle",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.webocVehicleNumber || " "}`,
    },
    {
      accessorKey: "driverName",
      header: "Driver",
      size: 130,
      accessorFn: (row: any) => ` ${row.driverName || " "}`,
    },
    {
      accessorKey: "vehicleType",
      header: "Type",
      type: "string",
      size: 120,
      accessorFn: (row: any) => ` ${row.vehicleType || " "}`,
    },
    {
      accessorKey: "vehicleStatus",
      header: "Status",
      size: 130,
      accessorFn: (row: any) => ` ${row.vehicleStatus || " "}`,
    },
    {
      accessorKey: "consignmentType",
      header: "Consignment ",
      description: "This column has a value getter and is not sortable.",
      sortable: true,
      size: 180,
      accessorFn: (row: any) => ` ${row.consignmentType || " "}`,
    },
    {
      accessorKey: "gdNumber",
      header: "GD#",
      size: 120,
      accessorFn: (row: any) => ` ${row.gdNumber || " "}`,
    },
    {
      accessorKey: "terminalLocation",
      header: "Location",
      size: 150,
      accessorFn: (row: any) => ` ${row.terminalLocation || " "}`,
    },
    {
      accessorKey: "cargoTypeName",
      header: "Cargo",
      size: 130,
      accessorFn: (row: any) => ` ${row.cargoTypeName || " "}`,
    },
    {
      accessorKey: "totalWeight",
      header: "Total Weight",
      type: "string",
      size: 170,
      accessorFn: (row: any) => ` ${row.totalWeight || " "}`,
    },
    {
      accessorKey: "netWeight",
      header: "Net Weight",
      type: "string",
      size: 170,
      accessorFn: (row: any) => ` ${row.netWeight || " "}`,
    },
    {
      accessorKey: "vehicleWeight",
      header: "Vehicle Weight",
      type: "string",
      size: 210,
      accessorFn: (row: any) => ` ${row.vehicleWeight || " "}`,
    },
    {
      accessorKey: "paymentReceived",
      header: "Payment Received",
      type: "string",
      size: 210,
      accessorFn: (row: any) => ` ${row.paymentReceived || " "}`,
      Footer: function PaymentFooter() {
        return (
          <Stack>
            Total Payment Received
            <Box color="warning.main">
              PKR{" "}
              {gridRecord
                ?.reduce((acc, curr) => acc + curr.paymentReceived, 0)
                .toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}
            </Box>
          </Stack>
        );
      },
    },
    {
      accessorKey: "totalPayment",
      header: "Total Payment",
      type: "string",
      size: 200,
      accessorFn: (row: any) => ` ${row.totalPayment || " "}`,
    },
    {
      accessorKey: "paymentType",
      header: "Payment Type",
      type: "string",
      size: 180,
      accessorFn: (row: any) => ` ${row.paymentType || " "}`,
    },
    {
      accessorKey: "yardName",
      header: "Yard Name",
      size: 160,
      accessorFn: (row: any) => ` ${row.yardName || " "}`,
    },
    {
      accessorKey: "rowName",
      header: "Row Name",
      size: 160,
      accessorFn: (row: any) => ` ${row.rowName || " "}`,
    },
    {
      accessorKey: "columnNumber",
      header: "Slot Number",
      size: 210,
      accessorFn: (row: any) => ` ${row.columnNumber || " "}`,
    },
    {
      accessorKey: "gateEntryDate",
      header: "Entry",
      size: 130,
      type: "dateTime",
      accessorFn: function customCell(row: any) {
        return `${
          row.gateEntryDate ? moment(row.gateEntryDate).format("DD-MM-YYYY hh:mm:ss A") : ""
        }`;
      },
    },
    {
      accessorKey: "gateOutDate",
      header: "Checkout",
      size: 150,
      type: "dateTime",
      accessorFn: function customCell(row: any) {
        return `${row.gateOutDate ? moment(row.gateOutDate).format("DD-MM-YYYY hh:mm:ss A") : ""}`;
      },
    },
    {
      accessorKey: "isWeightDone",
      header: "Weight",
      size: 140,
      type: "boolean",
      accessorFn: (row: any) => ` ${row.isWeightDone || " "}`,
    },
    {
      accessorKey: "isCommercialDone",
      header: "Commercial",
      size: 170,
      type: "boolean",
      accessorFn: (row: any) => ` ${row.isCommercialDone || " "}`,
    },
    {
      accessorKey: "isScanComplete",
      header: "Scan",
      size: 130,
      type: "boolean",
      accessorFn: (row: any) => ` ${row.isScanComplete || " "}`,
    },
    {
      accessorKey: "isAnomlyDetected",
      header: "Anomaly",
      size: 150,
      type: "boolean",
      accessorFn: (row: any) => ` ${row.isAnomlyDetected || " "}`,
    },
    {
      accessorKey: "vehicleInProgress",
      header: "In Progress ",
      size: 170,
      type: "boolean",
      accessorFn: (row: any) => ` ${row.vehicleInProgress || " "}`,
    },
  ];
  const classes = useStyles();
  const dispatch = useDispatch();

  const moduleName = "Generic Vehicle Report";
  const pageHeading = "Generic Vehicle Report";
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [gridRecord, setGridRecord] = React.useState<GridRowModel[]>([]);
  const [loader, setLoader] = useState<any>(false);
  const startDatePickerRef: any = useRef(null);
  const endDatePickerRef: any = useRef(null);
  const [terminalLocationArray, setTerminalLocationArray] = React.useState<any[]>([]);
  const [consignmentType, setconsignmentType] = React.useState<any>([]);
  const [vehicleType, setvehicleType] = React.useState<any>([]);
  const [typeOfCargoArray, settypeOfCargoArray] = React.useState<any>([]);
  const terminalLocationRef: any = useRef();
  const multiselectRef: any = useRef();
  const vehicelTypeMultiselectRef: any = useRef();
  const vehicelStatusMultiselectRef: any = useRef();
  const consignmentTypeMultiselectRef: any = useRef();
  const paymentTypeMultiselectRef: any = useRef();
  const [vehiclestatusList, setvehicleStatusList] = React.useState<any>([]);
  const [paymentTypesList, setPaymentTypesList] = useState<any>([]);
  const paymentTypes: any[] = useAppSelector(selectPaymentTypes);
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";
  const [isDrawerOpen, setisDrawerOpen] = React.useState(false);
  const loader2: any = useAppSelector(vehicleLoader);

  const handleCloseDrawer = () => {
    setisDrawerOpen(false);
  };
  const validationSchema = object({
    startDate: string().required("This field is required."),
    endDate: string().required("This field is required."),
    driverName: string()
      .max(50, "Maximum 50 characters allowed")
      .matches(/^[a-zA-Z ]*$/, "Only alphabets are allowed for this field "),
  });

  const formik = useFormik({
    initialValues: {
      vehicleRegNumber: "",
      consignmentTypeId: "",
      vehicleTypeId: "",
      cargoTypeIds: "",
      terminalLocationIds: "",
      driverName: "",
      vehicleStatusId: "",
      paymentType: "",
      startDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(0, 0)).format(
        "YYYY-MM-DDTHH:mm"
      ),
      endDate: moment(new Date(new Date().setDate(new Date().getDate())).setHours(23, 59)).format(
        "YYYY-MM-DDTHH:mm"
      ),
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const {
        startDate,
        endDate,
        vehicleRegNumber,
        consignmentTypeId,
        vehicleTypeId,
        cargoTypeIds,
        driverName,
        vehicleStatusId,
        paymentType,
        terminalLocationIds,
      } = values;

      const body = {
        vehicleRegNumber: vehicleRegNumber || null,
        consignmentTypeId: consignmentTypeId || null,
        vehicleTypeId: vehicleTypeId || null,
        cargoTypeIds: cargoTypeIds || null,
        driverName: driverName || null,
        vehicleStatusId: vehicleStatusId || null,
        paymentTypeId: paymentType || null,
        terminalLocationIds: terminalLocationIds || null,
        startDate: startDate || null,
        endDate: endDate || null,
      };
      // resetSearch();
      setGridRecord([]);
      setLoader(true);
      try {
        const results = await fetchWrapper.post("get-vehicle-generic-report", body);

        setGridRecord(results?.value?.gateEnteries || []);
      } catch (error) {
        setLoader(false);
      }
      setLoader(false);
    },
  });

  const resetSearch = () => {
    terminalLocationRef?.current ? terminalLocationRef.current?.getUpdated() : "";
    multiselectRef?.current ? multiselectRef.current?.getUpdated() : "";
    vehicelTypeMultiselectRef?.current ? vehicelTypeMultiselectRef.current?.getUpdated() : "";
    vehicelStatusMultiselectRef?.current ? vehicelStatusMultiselectRef.current?.getUpdated() : "";
    paymentTypeMultiselectRef?.current ? paymentTypeMultiselectRef.current?.getUpdated() : "";
    consignmentTypeMultiselectRef?.current
      ? consignmentTypeMultiselectRef.current?.getUpdated()
      : "";

    formik.resetForm();
    setGridRecord([]);
  };

  const getData = async () => {
    setLoader(true);
    try {
      if (paymentTypes.length === 0) dispatch(getPaymentTypesDropdownAction());
      const [locationResult, configResult]: [any, any] = await Promise.all([
        fetchWrapper.get("get-all-terminal-locations"),
        fetchWrapper.get("get-all-vehicle-configuration"),
      ]);
      setTerminalLocationArray(locationResult?.value || []);
      setconsignmentType(configResult?.value?.consignmentTypeData);
      setvehicleType(configResult?.value?.vehicleTypeData);
      settypeOfCargoArray(configResult?.value?.cargoTypeData);
      setvehicleStatusList(configResult?.value?.vehicleStatusData);

      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };

  const onMultiSelectSelection = (selectedList: any, selectedItem: any) => {
    const cargoTypes: string = selectedList.map((item: any) => (item.id ? item.id : "")).toString();
    formik.setFieldValue("cargoTypeIds", cargoTypes);
  };

  const onMultiSelectRemove = (selectedList: any, removedItem: any) => {
    const cargoTypes: string = selectedList.map((item: any) => (item.id ? item.id : "")).toString();
    formik.setFieldValue("cargoTypeIds", cargoTypes);
  };

  const vehicleTypeMultiSelectSelection = (selectedList: any, selectedItem: any) => {
    const vehicleType: string = selectedList
      .map((item: any) => (item.id ? item.id : ""))
      .toString();
    formik.setFieldValue("vehicleTypeId", vehicleType);
  };

  const vehicleTypeMultiSelectRemove = (selectedList: any, removedItem: any) => {
    const vehicleType: string = selectedList
      .map((item: any) => (item.id ? item.id : ""))
      .toString();
    formik.setFieldValue("vehicleTypeId", vehicleType);
  };

  const consignmentTypeMultiSelectSelection = (selectedList: any, selectedItem: any) => {
    const consignmentType: string = selectedList
      .map((item: any) => (item.id ? item.id : ""))
      .toString();
    formik.setFieldValue("consignmentTypeId", consignmentType);
  };

  const consignmentTypeMultiSelectRemove = (selectedList: any, removedItem: any) => {
    const consignmentType: string = selectedList
      .map((item: any) => (item.id ? item.id : ""))
      .toString();
    formik.setFieldValue("consignmentTypeId", consignmentType);
  };

  const vehicleStatusMultiSelectSelection = (selectedList: any, selectedItem: any) => {
    const vehicleStatus: string = selectedList
      .map((item: any) => (item.id ? item.id : ""))
      .toString();
    formik.setFieldValue("vehicleStatusId", vehicleStatus);
  };

  const vehicleStatusMultiSelectRemove = (selectedList: any, removedItem: any) => {
    const vehicleStatus: string = selectedList
      .map((item: any) => (item.id ? item.id : ""))
      .toString();
    formik.setFieldValue("vehicleStatusId", vehicleStatus);
  };

  const paymentTypeMultiSelectSelection = (selectedList: any, selectedItem: any) => {
    const vehicleStatus: string = selectedList
      .map((item: any) => (item.id ? item.id : ""))
      .toString();
    formik.setFieldValue("paymentType", vehicleStatus);
  };

  const paymentTypeMultiSelectRemove = (selectedList: any, removedItem: any) => {
    const vehicleStatus: string = selectedList
      .map((item: any) => (item.id ? item.id : ""))
      .toString();
    formik.setFieldValue("paymentType", vehicleStatus);
  };

  const terminalLocationMultiSelectSelection = (selectedList: any, selectedItem: any) => {
    const location: string = selectedList.map((item: any) => (item.id ? item.id : "")).toString();
    formik.setFieldValue("terminalLocationIds", location);
  };

  const terminalLocationMultiSelectRemove = (selectedList: any, removedItem: any) => {
    const location: string = selectedList.map((item: any) => (item.id ? item.id : "")).toString();
    formik.setFieldValue("terminalLocationIds", location);
  };

  const exportHandler = async (row: any) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );
    const exportedRecords = await row?.map((data: any) => {
      return {
        Vehicle: data?.vehicleNumber,
        Driver: data?.driverName,
        Type: data?.vehicleType,
        Status: data?.vehicleStatus,
        Consignment: data?.consignmentType,
        "Total Weight": `${data?.totalWeight} KG`,
        "Net Weight": `${data?.netWeight} KG`,
        "Vehicle Weight": `${data?.vehicleWeight} KG`,
        "Total Payment": `PKR ${data?.totalPayment}`,
        "Payment Received": `PKR ${data?.paymentReceived}`,
        "Payment Type": data?.paymentType,
        "GD #": data?.gdNumber,
        Location: data?.terminalLocation,
        Cargo: data?.cargoTypeName,
        "Yard Name": data?.yardName,
        "Row Name": data?.rowName,
        "Column Number": data?.columnNumber,
        Entry: data.gateEntryDate ? moment(data.gateEntryDate).format("DD-MM-YYYY hh:mm:ss A") : "",
        Checkout: data.gateOutDate ? moment(data.gateOutDate).format("DD-MM-YYYY hh:mm:ss A") : "",
        Anomaly: data.isAnomlyDetected ? "Yes" : "No",
        "In Progress": data.vehicleInProgress ? "Yes" : "No",
        Scan: data?.isScanComplete ? "Yes" : "No",
        Weight: data?.isWeightDone ? "Yes" : "No",
        Commercial: data?.isCommercialDone ? "Yes" : "No",
      };
    });
    return exportedRecords;
  };

  useEffect(() => {
    if (gridRecord.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "vehicleNumber"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <>
                    <MuiLink
                      className="customMuiLink"
                      onClick={() => {
                        dispatch(getVehicleDetailsByIdAction(row.original.gatePassId));
                        dispatch(setSelectedVehicleId(row.original.gatePassId));
                        setisDrawerOpen(true);
                      }}
                    >
                      {renderedCellValue}
                    </MuiLink>
                  </>
                );
              },
            }
          : column.accessorKey === "isScanComplete"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isScanComplete ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isScanComplete ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "vehicleInProgress"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.vehicleInProgress ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.vehicleInProgress ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "isAnomlyDetected"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isAnomlyDetected ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isAnomlyDetected ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "isWeightDone"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isWeightDone ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isWeightDone ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "isCommercialDone"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isCommercialDone ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isCommercialDone ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "totalPayment"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return ` ${"PKR "} ${row.original.totalPayment.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}`;
              },
            }
          : column.accessorKey === "paymentReceived"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return ` ${"PKR "} ${row.original.paymentReceived.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}`;
              },
            }
          : column.accessorKey === "totalWeight"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return `${row.original.totalWeight} ${" KG"}`;
              },
            }
          : column.accessorKey === "netWeight"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return `${row.original.netWeight} ${" KG"}`;
              },
            }
          : column.accessorKey === "vehicleWeight"
          ? {
              ...column,
              Cell: function customCell({ renderedCellValue, row }: any) {
                return `${row.original.vehicleWeight} ${" KG"}`;
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [gridRecord, formik.values.paymentType]);

  useEffect(() => {
    getData();
    gridRecord.length == 0 && formik.submitForm();
  }, []);

  useEffect(() => {
    const list = [
      ...paymentTypes.filter((item: any) => item.isFixed == 1),
      ...paymentTypes.filter((item: any) => item.isFixed == 0),
    ];
    setPaymentTypesList(
      list.concat({
        amount: 0,
        currency: "PKR",
        id: -1,
        isActive: true,
        isFixed: 0,
        name: "Other",
      })
    );
  }, [paymentTypes]);

  const transformPaymentTypesList = () => {
    return paymentTypesList.map((item: any) => {
      return {
        ...item,
        chargesType: item.isFixed === 0 ? "Variable Charges" : "Fixed Charges",
      };
    });
  };

  return (
    <>
      <TabHeader module={moduleName} heading={pageHeading} />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>
      {!loader2 && (
        <Drawer
          isDrawerOpen={isDrawerOpen}
          handleCloseDrawer={() => {
            handleCloseDrawer();
          }}
        />
      )}

      <Box m={2} />
      <Card>
        <CardContent className={classes.cardMain}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item md={9}>
              <Box mt={1} mb={2}>
                <Typography className={classes.pageHeading}> {pageHeading} </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3} direction="row">
              <Box textAlign="right" mt={1} mb={1}>
                <CommonButton cssClass="customSubmitBtn" onClick={() => resetSearch()}>
                  Reset
                </CommonButton>
                <CommonButton
                  cssClass="customSubmitBtn"
                  isGreen
                  onClick={() => formik.submitForm()}
                >
                  Search
                </CommonButton>
              </Box>
            </Grid>
          </Grid>

          <>
            <Grid spacing={1} container direction="row">
              <Grid item xs={12} md={3} lg={3}>
                <Box>
                  <CommonTextFieldDate
                    required
                    cssClassName="customTextField"
                    label={"Start Date"}
                    name={"startDate"}
                    datePickerRef={startDatePickerRef}
                    value={formik.values.startDate}
                    onChange={(e: any) => {
                      formik.setFieldValue("startDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                      formik.setFieldValue("endDate", "");
                    }}
                    maxDate={moment().format("YYYY-MM-DDTHH:mm")}
                    error={formik.touched.startDate && Boolean(formik.errors.startDate)}
                    helperText={formik.touched.startDate && formik.errors.startDate}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={3} lg={3}>
                <Box>
                  <CommonTextFieldDate
                    required
                    cssClassName="customTextField"
                    label={"End Date"}
                    name={"endDate"}
                    datePickerRef={endDatePickerRef}
                    value={formik.values.endDate}
                    onChange={(e: any) => {
                      formik.setFieldValue("endDate", moment(e).format("YYYY-MM-DDTHH:mm"));
                    }}
                    minDate={formik.values.startDate}
                    maxDate={moment(
                      new Date(new Date().setDate(new Date().getDate())).setHours(23, 59)
                    ).format("YYYY-MM-DDTHH:mm")}
                    error={formik.touched.endDate && Boolean(formik.errors.endDate)}
                    helperText={formik.touched.endDate && formik.errors.endDate}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={3} lg={3}>
                <Box>
                  <TextField
                    size="small"
                    fullWidth
                    className="customTextField"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Vehicle Number"
                    placeholder="Vehicle Number"
                    name="registrationNumber"
                    value={formik.values?.vehicleRegNumber}
                    onChange={(e: any) => {
                      formik.setFieldValue("vehicleRegNumber", e.target.value.toUpperCase());
                    }}
                    onKeyDownCapture={(e: any) => {
                      if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                    }}
                    onPaste={(e: any) => {
                      if (/[^A-Za-z0-9\d]/.test(e.clipboardData.getData("text"))) {
                        e.preventDefault();
                        return;
                      }
                    }}
                  />
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                lg={3}
                style={{
                  top: "5px",
                  position: "relative",
                  padding: "0 3px",
                  zIndex: 12,
                }}
              >
                <Box>
                  <TextField
                    className={"customTextField"}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onKeyDown={(event: any) => {
                      if (event.key !== "Tab") event.preventDefault();
                    }}
                    label="Type Of Consignment"
                    error={
                      formik.touched.consignmentTypeId && Boolean(formik.errors.consignmentTypeId)
                    }
                    helperText={formik.touched.consignmentTypeId && formik.errors.consignmentTypeId}
                  />
                  <Box
                    className={classes.multiselectConfigFileCompression}
                    style={{
                      bottom:
                        formik.touched.consignmentTypeId && Boolean(formik.errors.consignmentTypeId)
                          ? "55px"
                          : "35px",
                    }}
                  >
                    <MultiSelect2
                      allValues={consignmentType}
                      displayValue={"name"}
                      placeholder="Select Type Of Consignment"
                      onSelect={consignmentTypeMultiSelectSelection}
                      onRemove={consignmentTypeMultiSelectRemove}
                      ref={consignmentTypeMultiselectRef}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                lg={3}
                style={{
                  top: "5px",
                  position: "relative",
                  padding: "0 3px",
                  zIndex: 12,
                }}
              >
                <Box>
                  <TextField
                    className={"customTextField"}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onKeyDown={(event: any) => {
                      if (event.key !== "Tab") event.preventDefault();
                    }}
                    label="Vehicle Type"
                    error={formik.touched.vehicleTypeId && Boolean(formik.errors.vehicleTypeId)}
                    helperText={formik.touched.vehicleTypeId && formik.errors.vehicleTypeId}
                  />
                  <Box
                    className={classes.multiselectConfigFileCompression}
                    style={{
                      bottom:
                        formik.touched.vehicleTypeId && Boolean(formik.errors.vehicleTypeId)
                          ? "55px"
                          : "35px",
                    }}
                  >
                    <MultiSelect2
                      allValues={vehicleType}
                      displayValue={"name"}
                      placeholder="Select Vehicle Type"
                      onSelect={vehicleTypeMultiSelectSelection}
                      onRemove={vehicleTypeMultiSelectRemove}
                      ref={vehicelTypeMultiselectRef}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                lg={3}
                style={{
                  top: "5px",
                  position: "relative",
                  padding: "0 3px",
                  zIndex: 10,
                }}
              >
                <Box>
                  <TextField
                    className={"customTextField"}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onKeyDown={(event: any) => {
                      if (event.key !== "Tab") event.preventDefault();
                    }}
                    label="Type of Cargo"
                    error={formik.touched.cargoTypeIds && Boolean(formik.errors.cargoTypeIds)}
                    helperText={formik.touched.cargoTypeIds && formik.errors.cargoTypeIds}
                  />
                  <Box
                    className={classes.multiselectConfigFileCompression}
                    style={{
                      bottom:
                        formik.touched.cargoTypeIds && Boolean(formik.errors.cargoTypeIds)
                          ? "55px"
                          : "35px",
                    }}
                  >
                    <MultiSelect2
                      allValues={typeOfCargoArray}
                      // selctedItems={selectedCargoTypes}
                      displayValue={"name"}
                      placeholder="Select Type of Cargo"
                      onSelect={onMultiSelectSelection}
                      onRemove={onMultiSelectRemove}
                      ref={multiselectRef}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                lg={3}
                style={{
                  top: "5px",
                  position: "relative",
                  padding: "0 3px",
                  zIndex: 10,
                }}
              >
                <Box>
                  <TextField
                    className={"customTextField"}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onKeyDown={(event: any) => {
                      if (event.key !== "Tab") event.preventDefault();
                    }}
                    label="Vehicle Status"
                    error={formik.touched.vehicleStatusId && Boolean(formik.errors.vehicleStatusId)}
                    helperText={formik.touched.vehicleStatusId && formik.errors.vehicleStatusId}
                  />
                  <Box
                    className={classes.multiselectConfigFileCompression}
                    style={{
                      bottom:
                        formik.touched.vehicleStatusId && Boolean(formik.errors.vehicleStatusId)
                          ? "55px"
                          : "35px",
                    }}
                  >
                    <MultiSelect2
                      allValues={vehiclestatusList}
                      // selctedItems={selectedCargoTypes}
                      displayValue={"name"}
                      placeholder="Select Vehicle Status"
                      onSelect={vehicleStatusMultiSelectSelection}
                      onRemove={vehicleStatusMultiSelectRemove}
                      ref={vehicelStatusMultiselectRef}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                lg={3}
                style={{
                  top: "5px",
                  position: "relative",
                  padding: "0 3px",
                  zIndex: 10,
                }}
              >
                <Box>
                  <TextField
                    className={"customTextField"}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onKeyDown={(event: any) => {
                      if (event.key !== "Tab") event.preventDefault();
                    }}
                    label="Type of Payment"
                    error={formik.touched.paymentType && Boolean(formik.errors.paymentType)}
                    helperText={formik.touched.paymentType && formik.errors.paymentType}
                  />
                  <Box
                    className={classes.multiselectConfigFileCompression}
                    style={{
                      bottom:
                        formik.touched.paymentType && Boolean(formik.errors.paymentType)
                          ? "55px"
                          : "35px",
                    }}
                  >
                    <MultiSelect2
                      allValues={transformPaymentTypesList()}
                      renderGroup={"chargesType"}
                      displayValue={"name"}
                      placeholder="Select Type of Payment"
                      onSelect={paymentTypeMultiSelectSelection}
                      onRemove={paymentTypeMultiSelectRemove}
                      ref={paymentTypeMultiselectRef}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid
                item
                xs={12}
                md={3}
                lg={3}
                style={{
                  top: "5px",
                  position: "relative",
                  padding: "0 3px",
                  zIndex: 10,
                }}
              >
                <Box>
                  <TextField
                    className={"customTextField"}
                    size="small"
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    onKeyDown={(event: any) => {
                      if (event.key !== "Tab") event.preventDefault();
                    }}
                    label="Terminal Location"
                    error={
                      formik.touched.terminalLocationIds &&
                      Boolean(formik.errors.terminalLocationIds)
                    }
                    helperText={
                      formik.touched.terminalLocationIds && formik.errors.terminalLocationIds
                    }
                  />
                  <Box
                    className={classes.multiselectConfigFileCompression}
                    style={{
                      bottom:
                        formik.touched.terminalLocationIds &&
                        Boolean(formik.errors.terminalLocationIds)
                          ? "55px"
                          : "35px",
                    }}
                  >
                    <MultiSelect2
                      allValues={terminalLocationArray}
                      displayValue={"name"}
                      placeholder="Select Terminal Location"
                      onSelect={terminalLocationMultiSelectSelection}
                      onRemove={terminalLocationMultiSelectRemove}
                      ref={terminalLocationRef}
                    />
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={3} lg={3}>
                <Box>
                  <TextField
                    size="small"
                    fullWidth
                    className="customTextField"
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    label="Driver Name"
                    name="driverName"
                    id="driverName"
                    placeholder="Enter Driver Name"
                    value={formik.values.driverName}
                    onChange={(e) => formik.setFieldValue("driverName", e.target.value)}
                    onPaste={(e: any) => {
                      if (/[^A-Za-z ]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                    }}
                    onKeyDownCapture={(e: any) => {
                      if (/[^A-Za-z ]/.test(e.key)) e.preventDefault();
                    }}
                    error={formik.touched.driverName && Boolean(formik.errors.driverName)}
                    helperText={formik.touched.driverName && formik.errors.driverName}
                  />
                </Box>
              </Grid>
            </Grid>
          </>
          <CommonMaterialGrid
            rows={gridRecord}
            columns={displayColumns}
            defaultPerPageRecord={10}
            loader={loader}
            height={550}
            isExport={isExport}
            exportHandler={exportHandler}
            filename={`Generic_report_${Date.now()}`}
            isFooter={true}
          />
        </CardContent>
      </Card>
    </>
  );
}
